import type { Coaching } from '@efdigital/ll-addon-data/types/coaching/coaching';
import { CoachingPlanOversight } from '@efdigital/ll-admin-v2/components/coaching/coaching-plan-oversight.gts';
import { PaginationByOffset } from '@efdigital/ll-admin-v2/components/pagination.gts';
import { findCoachingPlansByCoachingId } from '@efdigital/ll-admin-v2/data/builders/xceed/coaching-plans';
import { getPaginationTotal } from '@efdigital/ll-admin-v2/helpers/get-pagination-total';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Request } from '@warp-drive/ember';

const PAGE_SIZE = 20;

interface EnrolledCoacheesSignature {
  Args: {
    coaching: Coaching;
    cohortId: string;
  };
  Element: HTMLDivElement;
}

/**
 * Paginated list of a coaching's enrolled coachees (plans). One page of plans
 * is fetched at a time server-side, so a cohort with hundreds of coachees
 * neither renders nor loads them all at once. Each row is a
 * CoachingPlanOversight, collapsed until the admin drills into partnerships,
 * sessions, and best-matched coaches.
 */
export class EnrolledCoachees extends Component<EnrolledCoacheesSignature> {
  @tracked offset = 0;
  limit = PAGE_SIZE;

  setOffset = (offset: number) => {
    this.offset = offset;
  };

  get plansQuery() {
    return findCoachingPlansByCoachingId(this.args.coaching.id!, {
      offset: this.offset,
      limit: this.limit,
    });
  }

  <template>
    <div ...attributes>
      <Request @query={{this.plansQuery}}>
        <:content as |content|>
          {{#if (getPaginationTotal content)}}
            <p class="text-muted small mb-2">
              {{getPaginationTotal content}}
              enrolled.
            </p>
            <div class="list-group mb-3">
              {{#each content.data as |plan|}}
                <CoachingPlanOversight
                  @plan={{plan}}
                  @coaching={{@coaching}}
                  @cohortId={{@cohortId}}
                />
              {{/each}}
            </div>
            <PaginationByOffset
              @offset={{this.offset}}
              @limit={{this.limit}}
              @total={{getPaginationTotal content}}
              @onOffsetChange={{this.setOffset}}
              class="d-flex flex-row justify-content-center"
            />
          {{else}}
            <p class="text-muted small mb-3">No coachees enrolled yet.</p>
          {{/if}}
        </:content>
        <:loading>
          <p class="text-muted small mb-3">Loading enrolled coachees…</p>
        </:loading>
      </Request>
    </div>
  </template>
}
